import {
  revalidateAllApiResourceRoutes,
  revalidateApiCollection,
  revalidateApiGet,
} from "../cache/revalidate";
import type {
  ApiCollectionScopedRoute,
  ApiResourceDescriptor,
  ApiResourceRoute,
} from "./types";

type ResourceRouteData<TEntity> = Readonly<{
  route: string;
  collectionRoute?: string;
  descriptor: ApiResourceDescriptor<TEntity>;
}>;

async function revalidateCollection(this: ApiResourceRoute) {
  await revalidateApiCollection(this);
}

async function revalidateGet(this: ApiResourceRoute) {
  await revalidateApiGet(this);
}

async function revalidateAll(this: ApiResourceRoute) {
  await revalidateAllApiResourceRoutes(this);
}

function addRevalidators<TEntity>(
  route: ResourceRouteData<TEntity>,
  revalidate: (this: ApiResourceRoute<TEntity>) => Promise<void>,
) {
  Object.defineProperties(route, {
    revalidate: { value: revalidate },
    revalidateAll: { value: revalidateAll },
  });
}

export function createCollectionResourceRoute<TEntity>(
  route: ResourceRouteData<TEntity> & Readonly<{ collectionRoute: string }>,
): ApiCollectionScopedRoute<TEntity> {
  addRevalidators(route, revalidateCollection);
  return route as ApiCollectionScopedRoute<TEntity>;
}

export function createDetailResourceRoute<TEntity>(
  route: ResourceRouteData<TEntity> & Readonly<{ collectionRoute: string }>,
): ApiCollectionScopedRoute<TEntity>;
export function createDetailResourceRoute<TEntity>(
  route: ResourceRouteData<TEntity>,
): ApiResourceRoute<TEntity>;
export function createDetailResourceRoute<TEntity>(
  route: ResourceRouteData<TEntity>,
) {
  addRevalidators(route, revalidateGet);
  return route as ApiResourceRoute<TEntity>;
}

export function createQueriedResourceRoute<TEntity>(
  resourceRoute: ApiResourceRoute<TEntity>,
  route: string,
) {
  const routeData = {
    route,
    collectionRoute: resourceRoute.collectionRoute,
    descriptor: resourceRoute.descriptor,
  };

  const collectionRoute = resourceRoute.collectionRoute;
  const isCollectionRoute =
    collectionRoute != null &&
    (resourceRoute.route === collectionRoute ||
      resourceRoute.route.startsWith(`${collectionRoute}?`));

  if (isCollectionRoute) {
    return createCollectionResourceRoute({
      ...routeData,
      collectionRoute,
    });
  }

  return createDetailResourceRoute(routeData);
}
